import { joiResolver } from '@hookform/resolvers/joi';

import { makeStyles } from 'tss-react/mui';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  DatePicker,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorInfo,
  Grid,
  HtmlTrans, PAYLOAD_DATE_FORMAT,
  stringToPayloadFormat,
  stringToSemanticDisplay,
  useSnackbarHandler,
} from 'web-library';
import ToggleDisplayEditFormItem from '../../../../components/ToggleDisplayEditFormItem';
import {
  ValidCeqTestDateModelResponse
} from '../../../../api-refactored/exam-configuration/useGetValidCeqTestDates';
import { ValidCeqTestDateFormData, ValidCeqTestDateSchema } from './schema/ValidCeqTestDateSchema';
import {
  usePostCreateValidCeqTestDates
} from '../../../../api-refactored/exam-configuration/usePostCreateValidCeqTestDates';
import {
  usePostUpdateValidCeqTestDates
} from '../../../../api-refactored/exam-configuration/usePostUpdateValidCeqTestDates';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";
import dayjs from "dayjs";

const useStyles = makeStyles()((theme) =>
({
  actionContainer: {
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  title: {
    color: theme.color.text.textPrimary,
    paddingBottom: theme.spacing(3),
  },
  formFieldContainer: {
    paddingBottom: theme.spacing(3),
  },
  dividerContainer: {
    paddingBottom: theme.spacing(3),
  },
  datePickerContainer: {
    marginBottom: theme.spacing(3),
  },
}));

interface ValidCeqTestDateModalProps {
  registrationExamLevelUuid: string;
  isStrictlyAeisExam: boolean;
  year: number;
  isEditState: boolean;
  isModalOpened: boolean;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  fetchValidCeqTestDateListing: () => void;
  defaultData?: ValidCeqTestDateModelResponse;
}

const ValidCeqTestDateModal = ({
  registrationExamLevelUuid,
  isStrictlyAeisExam,
  year,
  isEditState,
  isModalOpened,
  setModalOpened,
  fetchValidCeqTestDateListing,
  defaultData,
}: ValidCeqTestDateModalProps) => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { classes } = useStyles();
  const [apiErrorMsg, setApiErrorMsg] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();

  useEffect(() => {
    if (isModalOpened) {
      // To reset the default value of the state every time modal is opened.
      if (defaultData) {
        setStartDate(defaultData.firstAcceptedCeqTestDate);
        setEndDate(defaultData.lastAcceptedCeqTestDate);

        setValue('firstAcceptedCeqTestDate',  defaultData.firstAcceptedCeqTestDate);
        setValue('lastAcceptedCeqTestDate',  defaultData.lastAcceptedCeqTestDate);
      } else {
        setStartDate(undefined);
        setEndDate(undefined);

        setValue('firstAcceptedCeqTestDate', '');
        setValue('lastAcceptedCeqTestDate',  '');
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isModalOpened]);

  const formHook = useForm<ValidCeqTestDateFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(ValidCeqTestDateSchema),
  });
  const { handleSubmit, formState: { errors }, reset: formReset, setValue, control } = formHook;

  const {
    mutate: createValidCeqTestDatesMutate,
    reset: createValidCeqTestDatesReset,
    status: createValidCeqTestDatesStatus,
  } = usePostCreateValidCeqTestDates({
    onSuccess: () => {
      fetchValidCeqTestDateListing();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: (
          <HtmlTrans
            i18nKey="exam->>ValidCeqTestDateConfiguration.ValidCeqTestDateCreated"
            values={{
              examLevelType: isStrictlyAeisExam ? 'AEIS' : 'S-AEIS',
              admissionYear: year,
            }}
          />
        ),
      });
      onDialogCloseHandler();
    },
    onError: (err) => {
      setApiErrorMsg(err.response?.data.message!);
    },
  });

  const {
    mutate: updateValidCeqTestDatesMutate,
    reset: updateValidCeqTestDatesReset,
    status: updateValidCeqTestDatesStatus,
  } = usePostUpdateValidCeqTestDates({
    onSuccess: () => {
      fetchValidCeqTestDateListing();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: (
          <HtmlTrans
            i18nKey="exam->>ValidCeqTestDateConfiguration.ValidCeqTestDateUpdated"
            values={{
              examLevelType: isStrictlyAeisExam ? 'AEIS' : 'S-AEIS',
              admissionYear: year,
            }}
          />
        ),
      });
      onDialogCloseHandler();
    },
    onError: (err) => {
      setApiErrorMsg(err.response?.data.message!);
    },
  });

  const onDialogCloseHandler = () => {
    setModalOpened(false);
    formReset();
    createValidCeqTestDatesReset();
    updateValidCeqTestDatesReset();
    setApiErrorMsg(null);
  };

  const onSubmitHandler = (formData: ValidCeqTestDateFormData) => {
    setApiErrorMsg(null);
    const baseRequest = {
      firstAcceptedCeqTestDate: stringToPayloadFormat(formData.firstAcceptedCeqTestDate),
      lastAcceptedCeqTestDate: stringToPayloadFormat(formData.lastAcceptedCeqTestDate),
    };

    if (isEditState) {
      updateValidCeqTestDatesMutate({
        ...baseRequest,
        registrationExamLevelUuid,
        validCeqTestDateUuid: defaultData?.uuid || '',
      });
    } else {
      createValidCeqTestDatesMutate({
        ...baseRequest,
        registrationExamLevelUuid,
        admissionYear: year,
      });
    }
  };

  return (
    <Dialog id="aeis-exam-fee-modal" open={isModalOpened} maxWidth="md">
      {isEditState ? (
        <DialogTitle>
          {t('exam->>ValidCeqTestDateConfiguration.EditValidCeqTestDateModalTitle')}
        </DialogTitle>
      ) : (
        <DialogTitle>
          {t('exam->>ValidCeqTestDateConfiguration.AddValidCeqTestDateModalTitle')}
        </DialogTitle>
      )}
      <DialogContent>
        <form id="valid-ceq-test-date-form" onSubmit={handleSubmit(onSubmitHandler)}>
          <ToggleDisplayEditFormItem
            containerClassName={!isEditState ? classes.formFieldContainer : ''}
            labelText={t('exam->>ExamConfiguration.ExamType')}
            valueText={isStrictlyAeisExam ? 'AEIS' : 'S-AEIS'}
          />
          <ToggleDisplayEditFormItem
            containerClassName={!isEditState ? classes.formFieldContainer : ''}
            labelText={t('exam->>ExamConfiguration.YearOfAdmission')}
            valueText={year.toString()}
          />
          <Grid className={classes.datePickerContainer} container spacing={4} direction="row">
            <Grid md={6} sm={12} xs={12}>
              <ControlledDatePicker
                  control={control}
                name="firstAcceptedCeqTestDate"
                label={t('exam->>ValidCeqTestDateConfiguration.FirstAcceptedCeqTestDate')}
                // labelFunc={() => (startDate ? stringToSemanticDisplay(startDate) : '')}
                onChange={(date) => {
                  setStartDate(date || '');
                  setValue('firstAcceptedCeqTestDate',  date || '');
                }}
                error={!!errors.firstAcceptedCeqTestDate}
                helperText={errors?.firstAcceptedCeqTestDate?.message?.toString()}
                inputVariant="filled"
                autoOk
              />
            </Grid>
            <Grid md={6} sm={12} xs={12}>
              <ControlledDatePicker
                  control={control}
                name="lastAcceptedCeqTestDate"
                label={t('exam->>ValidCeqTestDateConfiguration.LastAcceptedCeqTestDate')}
                // labelFunc={() => (endDate ? stringToSemanticDisplay(endDate) : '')}
                onChange={(date) => {
                  setEndDate(date || '');
                  setValue('lastAcceptedCeqTestDate', date || '');
                }}
                error={!!errors.lastAcceptedCeqTestDate}
                helperText={errors?.lastAcceptedCeqTestDate?.message?.toString()}
                inputVariant="filled"
                autoOk
              />
            </Grid>
          </Grid>
        </form>
        {apiErrorMsg && <ErrorInfo label={apiErrorMsg} />}
      </DialogContent>
      <DialogActions>
        <Box className={classes.actionContainer}>
          <Button type="button" variant="text" onClick={onDialogCloseHandler}>
            {t('exam->>ExamConfiguration.Cancel')}
          </Button>
          <ApiButton
            type="submit"
            status={isEditState ? updateValidCeqTestDatesStatus : createValidCeqTestDatesStatus}
            form="valid-ceq-test-date-form"
          >
            {t('exam->>ValidCeqTestDateConfiguration.ModalConfirmButton')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
export default ValidCeqTestDateModal;
